import * as React from "react";
import { Box, TextField, List, ListItemButton, ListItemText, Typography } from "@mui/material";
import { QueryStateBoundary } from "@/components/dashboard/QueryStateBoundary";
import { useInstrumentSearch } from "@/hooks/queries/useInstrumentSearch";

export interface InstrumentSummary {
  instrument_token: string;
  trading_symbol: string;
  exchange_code: string;
}

interface InstrumentSearchProps {
  onSelect: (instrument: InstrumentSummary) => void;
}

/**
 * Search only - selecting a result never places or previews an order by
 * itself, it only feeds the Quote Panel/Order Ticket's instrument field.
 * `InstrumentSummaryDto` has no distinct Instrument Type or Provider
 * field, so neither column is shown here (rendering one would mean
 * fabricating data the backend does not supply).
 */
export function InstrumentSearch({ onSelect }: InstrumentSearchProps): React.JSX.Element {
  const [term, setTerm] = React.useState("");
  const searchQuery = useInstrumentSearch({ tradingSymbol: term || undefined }, term.length > 0);

  return (
    <Box>
      <TextField
        fullWidth
        size="small"
        label="Search instruments by symbol"
        value={term}
        onChange={(event) => setTerm(event.target.value)}
      />
      {term.length === 0 ? (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
          Type a trading symbol to search.
        </Typography>
      ) : (
        <QueryStateBoundary
          query={searchQuery}
          isEmpty={(data) => (data as unknown as InstrumentSummary[]).length === 0}
          emptyMessage="No instruments match this search."
        >
          {(payload) => {
            const results = payload as unknown as InstrumentSummary[];
            return (
              <List dense aria-label="Instrument search results">
                {results.map((instrument) => (
                  <ListItemButton
                    key={instrument.instrument_token}
                    onClick={() => onSelect(instrument)}
                  >
                    <ListItemText
                      primary={instrument.trading_symbol}
                      secondary={instrument.exchange_code}
                    />
                  </ListItemButton>
                ))}
              </List>
            );
          }}
        </QueryStateBoundary>
      )}
    </Box>
  );
}
